// GenericStepModal.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React, { useState, useEffect, useCallback, useMemo, useRef, useImperativeHandle, forwardRef, useId } from 'react';
import { Modal, Button, Steps, Tabs, Flex, Card, Space, Tooltip, ConfigProvider, Watermark } from 'antd';
import { MemoryRouter, useInRouterContext } from 'react-router-dom';
import { LeftOutlined, RightOutlined, CheckOutlined } from '@ant-design/icons';
import {
  SimpleFormProvider,
  useSimpleFormContext,
} from 'components/SimpleComponents/SimpleFormProvider';
import useFormInstance from 'hooks/useFormInstance';
import useZustand from 'hooks/useZustand';
import translateX from 'utils/translateX';
import { darkTheme, lightTheme } from 'configs/appEngine';
import appService from 'services/appService';
// import './GenericModal.css';

// *******************************************************************************
// FORM COMPONENT
// *******************************************************************************
const StepForm = forwardRef(({
  steps,
  currentStep,
  visibleSteps,
  visibleCurrentStep,
  goNext,
  isSingleStep,
}, ref) => {
  const { form, goFocus, validateForm, addFieldsWithError } = useSimpleFormContext();

  // Expose addFieldsWithError to parent via ref
  useImperativeHandle(ref, () => ({
    addFieldsWithError,
  }), [addFieldsWithError]);

  // Render current step fields
  const currentStepConfig = steps[currentStep];

  return (
    <>
      {/* Steps indicator - only shows when there are multiple visible steps */}
      {!isSingleStep && (
        <Card
          style={{ width: '100%', margin: '14px 0' }}
          styles={{ body: { padding: '12px 16px' } }}
        >
          <Steps
            current={visibleCurrentStep}
            type="dot"
            size="small"
            responsive={false}
            style={{
              marginTop: 18,
              width: '100%',
            }}
            items={visibleSteps.map((step, index) => {
              const title = translateX(step.stepTitle) || `${translateX('step')} ${index + 1}`;
              const tooltip = step.stepTooltip ? translateX(step.stepTooltip) : null;

              return {
                title: tooltip ? (
                  <Tooltip title={tooltip} placement="top">
                    <span className="step-tooltip-wrapper">{title}</span>
                  </Tooltip>
                ) : (
                  title
                ),
              };
            })}
          />
        </Card>
      )}

      {/* Tabs for content (hidden navigation) */}
      <Tabs
        className="force-hide-tab-nav"
        activeKey={String(currentStep)}
        renderTabBar={() => null} // Hide tab navigation
        items={steps.map((step, index) => ({
          key: String(index),
          label: step.stepTitle,
          children: (
            // <div className="step-content">
            <div>
              {typeof step.content === 'function'
                ? step.content({ form, goFocus, validateForm, goNext })
                : step.content}
            </div>
          ),
        }))}
      />
    </>
  );
});

// *******************************************************************************
// FOOTER COMPONENT
// *******************************************************************************
const FooterButtons = ({
  currentStep,
  totalSteps,
  loading,
  onCancel,
  onPrevious,
  onNext,
  onConfirm,
  steps,
  isConfirmStep, // Allows dynamic confirm detection (e.g., keyUser skips step 3)
  disableBack = false, // Disable back button (useful for edit mode)
  initialStep = 0, // Track initial step for back button logic
  confirmDisabled = false, // Disable confirm button (useful for edit mode when no changes)
  isSingleStep = false, // Hide Previous button for single-step modals
  customLeftButtons = [], // Custom buttons to display on the left side of the footer
}) => {
  // Can't go back if: first step, OR disableBack is true (regardless of current step)
  const isFirstStep = currentStep === 0 || disableBack;
  const isLastStep = currentStep === totalSteps - 1;

  const currentStepConfig = steps[currentStep];

  // Check if this is a confirm step (either last step or dynamically set via isConfirmStep)
  const shouldConfirm = isConfirmStep ?? isLastStep;

  // Button text: use step config, or 'confirm' if confirm step, or 'next'
  const nextButtonText = currentStepConfig?.nextButtonText || (shouldConfirm ? 'confirm' : 'next');

  // Button props from step config
  const nextButtonProps = currentStepConfig?.nextButtonProps || {};

  // Determine icon: custom from props, or CheckOutlined for confirm, or RightOutlined for next
  const defaultIcon = shouldConfirm ? <CheckOutlined /> : <RightOutlined />;
  const nextButtonIcon = nextButtonProps?.icon || currentStepConfig?.nextButtonIcon || defaultIcon;

  // Danger only if explicitly set in props (not automatic)
  const isDanger = nextButtonProps?.danger === true;

  // Use ghost for intermediate steps (border + text in primary color, transparent bg)
  // For single-step modals, always use solid button (not ghost)
  const isGhost = !shouldConfirm && !isSingleStep;

  // Determine layout: use space-between only when there are left buttons
  const hasLeftButtons = customLeftButtons.length > 0;

  return (
    <Flex justify={hasLeftButtons ? 'space-between' : 'end'} align="middle" style={{ paddingTop: 14, width: '100%' }}>
      {/* Left side - Custom buttons (only render if there are buttons) */}
      {hasLeftButtons && (
        <Space>
          {customLeftButtons.map((btn, idx) => {
            const button = (
              <Button
                key={idx}
                onClick={btn.onClick}
                icon={btn.icon}
                type={btn.type || 'default'}
                danger={btn.danger}
                disabled={loading}
              >
                {btn.label ? translateX(btn.label) : null}
              </Button>
            );
            return btn.tooltip
              ? <Tooltip key={idx} title={translateX(btn.tooltip)}>{button}</Tooltip>
              : button;
          })}
        </Space>
      )}

      {/* Right side - Navigation buttons */}
      <Space>
        {/* Hide Previous button for single-step modals */}
        {!isSingleStep && (
          <Button onClick={onPrevious} disabled={loading || isFirstStep} icon={<LeftOutlined />}>
            {translateX('previous')}
          </Button>
        )}

        <Button
          type="primary"
          loading={loading}
          onClick={shouldConfirm ? onConfirm : onNext}
          icon={nextButtonIcon}
          danger={isDanger}
          disabled={shouldConfirm && confirmDisabled}
          ghost={isGhost}
          {...nextButtonProps}
        >
          {translateX(nextButtonText)}
        </Button>
      </Space>
    </Flex>
  );
};

// *******************************************************************************
// ROUTER WRAPPER
// *******************************************************************************
// Wrapper that only adds MemoryRouter if not already inside a Router context
// This prevents the "Router inside Router" error when GenericModal is used
// directly as a component (vs rendered via createRoot/openModal)
const RouterWrapper = ({ children }) => {
  const isInRouter = useInRouterContext();
  if (isInRouter) {
    return children;
  }
  return <MemoryRouter>{children}</MemoryRouter>;
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const GenericModal = ({
  show,
  setShow,
  title,
  icon,
  steps = [],
  onFinish,
  initialValues = {},
  formProps = {},
  width = 520,
  afterClose: externalAfterClose, // PERFORMANCE FIX: Allow parent to be notified after animation completes
  initialStep = 0, // Start at a specific step (useful for edit mode)
  disableBack = false, // Disable the back button (useful for edit mode)
  confirmDisabled = false, // Disable confirm button (useful for edit mode when no changes)
  onValuesChange, // Callback when form values change (changedValues, allValues)
  maskClosable = true, // Allow closing modal by clicking outside (default: true)
  requiredFields: requiredFieldsProp, // Required fields for validation (supports 'uniqueKey' alias)
  closable = true, // Show/hide the X close button (default: true)
  customLeftButtons = [], // Custom buttons for the left side of the footer
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const modalFormId = useId(); // Unique ID for form name to avoid conflicts
  const { formRef, resetForm, form, validateForm, goFocus, clearValidationState } = useFormInstance(
    {
      data: initialValues,
    }
  );

  // Ref to access StepForm's addFieldsWithError from SimpleFormProvider context
  const stepFormRef = useRef(null);

  // Ref to track this modal's content element for keyboard event handling
  const modalContentRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isConfirmStep, setIsConfirmStep] = useState(false); // Dynamic confirm detection
  const [formVersion, setFormVersion] = useState(0); // Tracks form changes to trigger re-renders

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (show) {
      setCurrentStep(initialStep);
    }
  }, [show, initialStep]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. COMPUTED VALUES
  // ─────────────────────────────────────────────────────────────────────────────
  // Filter visible steps (steps with hidden=false or hidden function returning false)
  // formVersion is used to trigger re-calculation when form values change
  const visibleSteps = useMemo(() => {
    return steps.filter((step, index) => {
      if (typeof step.hidden === 'function') {
        return !step.hidden({ form, currentStep: index });
      }
      return !step.hidden;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps, form, formVersion]);

  // Map currentStep to visible step index
  const visibleCurrentStep = useMemo(() => {
    let visibleIndex = 0;
    for (let i = 0; i < currentStep; i++) {
      const step = steps[i];
      const isHidden =
        typeof step.hidden === 'function' ? step.hidden({ form, currentStep: i }) : step.hidden;
      if (!isHidden) {
        visibleIndex++;
      }
    }
    return visibleIndex;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps, currentStep, form, formVersion]);

  // Detect single-step modal (hide steps indicator and Previous button)
  const isSingleStep = visibleSteps.length <= 1;

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    if (!loading) {
      setShow(false);
    }
  }, [loading, setShow]);

  const handlePrevious = useCallback(() => {
    // Don't go back if disableBack is true and we're at or before initialStep
    if (disableBack && currentStep <= initialStep) {
      return;
    }
    if (currentStep > 0) {
      // Remove focus from active element to prevent aria-hidden warning during navigation
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      // Find previous visible step (skip hidden steps)
      let prevStep = currentStep - 1;
      while (prevStep >= 0) {
        const step = steps[prevStep];
        const isHidden = typeof step.hidden === 'function'
          ? step.hidden({ form, currentStep: prevStep })
          : step.hidden;
        if (!isHidden) {
          break; // Found a visible step
        }
        prevStep--;
      }

      if (prevStep >= 0) {
        setCurrentStep(prevStep);
      }
    }
  }, [currentStep, disableBack, initialStep, steps, form]);

  const handleNext = useCallback(async () => {
    const currentStepConfig = steps[currentStep];

    // Validate current step fields if specified
    if (currentStepConfig.validationFields?.length > 0) {
      if (!validateForm) {
        console.error('[GenericStepModal] validateForm is not available');
        return;
      }

      const { valid, errors } = await validateForm(currentStepConfig.validationFields);
      if (!valid) {
        // Propagate errors to fieldsWithError context so SimpleComponents
        // with custom validateStatus (e.g., SimpleCnpj) show error state
        const fieldErrors = {};
        (errors || []).forEach(e => {
          const fieldName = Array.isArray(e.name) ? e.name.join('.') : e.name;
          fieldErrors[fieldName] = 'required';
        });
        stepFormRef.current?.addFieldsWithError?.(fieldErrors);
        return;
      }
    }

    // Execute custom onNext callback if provided
    if (currentStepConfig.onNext) {
      setLoading(true);
      try {
        const canProceed = await currentStepConfig.onNext({
          form,
          validateForm,
          goFocus,
          currentStep,
          setLoading,
        });

        // If onNext returns false, don't proceed to next step
        if (canProceed === false) {
          return;
        }
      } catch (error) {
        console.error('[GenericStepModal] onNext error:', error);
        return;
      } finally {
        setLoading(false);
      }
    }

    // Clear validation state before advancing to prevent step 2 fields from showing errors
    clearValidationState?.();

    // Find next visible step (skip hidden steps)
    let nextStepIndex = currentStep + 1;
    while (nextStepIndex < steps.length) {
      const step = steps[nextStepIndex];
      const isHidden = typeof step.hidden === 'function'
        ? step.hidden({ form, currentStep: nextStepIndex })
        : step.hidden;
      if (!isHidden) {
        break; // Found a visible step
      }
      nextStepIndex++;
    }

    // Clear validation errors for next step's fields before advancing
    if (nextStepIndex < steps.length) {
      const nextStepConfig = steps[nextStepIndex];
      if (nextStepConfig.validationFields?.length > 0) {
        const fieldsToReset = nextStepConfig.validationFields.map(fieldName => ({
          name: fieldName,
          errors: [],
          touched: false, // Mark as untouched to prevent showing validation errors
        }));
        form?.setFields(fieldsToReset);
      }
    }

    // Remove focus from active element to prevent aria-hidden warning during navigation
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    // Move to next visible step
    if (nextStepIndex < steps.length) {
      setCurrentStep(nextStepIndex);
    }
  }, [steps, currentStep, validateForm, goFocus, form, clearValidationState]);

  const handleConfirm = useCallback(async () => {
    // Remove focus from active element to prevent aria-hidden warning
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    const lastStepConfig = steps[currentStep];

    // Validate last step fields if specified
    if (lastStepConfig.validationFields?.length > 0) {
      if (!validateForm) {
        console.error('[GenericStepModal] validateForm is not available');
        return;
      }

      const { valid, errors } = await validateForm(lastStepConfig.validationFields);
      if (!valid) {
        // Propagate errors to fieldsWithError context so SimpleComponents
        // with custom validateStatus (e.g., SimpleCnpj) show error state
        const fieldErrors = {};
        (errors || []).forEach(e => {
          const fieldName = Array.isArray(e.name) ? e.name.join('.') : e.name;
          fieldErrors[fieldName] = 'required';
        });
        stepFormRef.current?.addFieldsWithError?.(fieldErrors);
        return;
      }
    }

    // Validate required fields from requiredFieldsProp (supports 'uniqueKey' alias)
    const requiredFields = requiredFieldsProp || formProps?.requiredFields;

    if (requiredFields?.length > 0) {
      const formValues = form?.getFieldsValue(true) || {};

      // Resolve 'uniqueKey' alias to actual document field based on entityType and countryCode
      // Also pass entityState from formProps as fallback when form values aren't set yet
      const resolvedFields = resolveRequiredFields(requiredFields, formValues, formProps?.entityState);

      // Check each required field
      const missingFields = [];
      for (const fieldName of resolvedFields) {
        const value = getNestedValue(formValues, fieldName);
        const isEmpty = isValueEmpty(value);
        if (isEmpty) {
          missingFields.push(fieldName);
        }
      }

      if (missingFields.length > 0) {
        // Use SimpleFormProvider's addFieldsWithError for consistent error handling and focus
        const errors = {};
        missingFields.forEach(fieldName => {
          // Convert dot notation to flatten format (e.g., 'cnpj.value' stays as is)
          errors[fieldName] = 'required_field';
        });

        // This will set errors, trigger hasError, and focus on first field
        stepFormRef.current?.addFieldsWithError?.(errors);

        return; // Don't proceed with confirmation
      }
    }

    // Execute custom onNext callback if provided (for confirm step validation like "enable NFCe first")
    if (lastStepConfig.onNext) {
      setLoading(true);
      try {
        const canProceed = await lastStepConfig.onNext({
          form,
          validateForm,
          goFocus,
          currentStep,
          setLoading,
        });

        // If onNext returns false, don't proceed with confirmation
        if (canProceed === false) {
          setLoading(false);
          return;
        }
      } catch (error) {
        console.error('[GenericStepModal] onNext error on confirm:', error);
        setLoading(false);
        return;
      }
      // Note: Don't setLoading(false) here - onFinish will handle it
    }

    setLoading(true);
    try {
      const result = await onFinish?.({ form, validateForm, goFocus, setLoading });
      // Only close modal if onFinish doesn't explicitly return false
      if (result !== false) {
        setShow(false);
      }
    } catch (error) {
      console.error('[GenericStepModal] onFinish error:', error);
    } finally {
      setLoading(false);
    }
  }, [steps, currentStep, validateForm, goFocus, form, onFinish, setShow, requiredFieldsProp, formProps]);

  const handleAfterClose = useCallback(() => {
    resetForm();
    setCurrentStep(0);
    setLoading(false);
    setIsConfirmStep(false);
    setFormVersion(0);

    // PERFORMANCE FIX: Notify parent that animation completed
    if (externalAfterClose) {
      externalAfterClose();
    }
  }, [resetForm, externalAfterClose]);

  // Internal handler for form value changes - increments formVersion to trigger re-renders
  const handleInternalValuesChange = useCallback(
    (changedValues, allValues) => {
      // Increment formVersion to trigger re-calculation of visibleSteps and isConfirmStep
      setFormVersion(v => v + 1);

      // Call external callback if provided
      onValuesChange?.(changedValues, allValues);
    },
    [onValuesChange]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Execute onEnter callback when step changes (NOT when form values change)
  useEffect(() => {
    if (!show) return;

    const stepConfig = steps[currentStep];

    if (stepConfig?.onEnter) {
      stepConfig.onEnter({ form, validateForm, goFocus, currentStep, setIsConfirmStep });
    }
  }, [currentStep, show, steps, form, validateForm, goFocus]);

  // Check for dynamic isConfirmStep - re-evaluated when form values change
  // Considers visible steps only (hidden steps are not counted)
  useEffect(() => {
    if (!show) return;

    const stepConfig = steps[currentStep];

    if (typeof stepConfig?.isConfirmStep === 'function') {
      const shouldConfirm = stepConfig.isConfirmStep({ form, currentStep });
      setIsConfirmStep(shouldConfirm);
    } else if (typeof stepConfig?.isConfirmStep === 'boolean') {
      setIsConfirmStep(stepConfig.isConfirmStep);
    } else {
      // Default: confirm on last VISIBLE step (not last step in array)
      // This ensures hidden steps don't affect the confirm button logic
      const isLastVisibleStep = visibleCurrentStep === visibleSteps.length - 1;
      setIsConfirmStep(isLastVisibleStep);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, show, steps, formVersion, visibleCurrentStep, visibleSteps.length]);

  // Handle ENTER key press to submit/next and ESC to close
  useEffect(() => {
    const handleKeyDown = e => {
      // Check if this modal is the topmost one (not covered by another modal)
      // Get all visible modal wraps and check if this modal is the last (topmost) one
      const allModalWraps = document.querySelectorAll('.ant-modal-wrap:not([style*="display: none"])');
      if (allModalWraps.length > 1) {
        // Multiple modals open - only the topmost should handle keyboard events
        const topmostModal = allModalWraps[allModalWraps.length - 1];

        // Check if THIS modal's content is inside the topmost modal wrap
        const thisModalWrap = modalContentRef.current?.closest('.ant-modal-wrap');
        if (!thisModalWrap || thisModalWrap !== topmostModal) {
          return; // Not the topmost modal, ignore keyboard events
        }
      }

      // ESC to close (only if not loading and closable)
      if (e.key === 'Escape' && !loading && closable) {
        e.preventDefault();
        handleClose();
        return;
      }

      // ENTER to next/confirm (uses dynamic isConfirmStep)
      if (e.key === 'Enter' && !e.shiftKey && !loading) {
        // Check if focus is inside a Select component - let Select handle Enter (open/close/select)
        const isSelectFocused = document.activeElement?.closest('.ant-select') !== null;
        if (isSelectFocused) {
          return; // Let the Select component handle Enter key
        }

        e.preventDefault();
        if (isConfirmStep) {
          // Don't confirm if confirmDisabled is true
          if (!confirmDisabled) {
            handleConfirm();
          }
        } else {
          handleNext();
        }
      }
    };

    if (show) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [show, loading, isConfirmStep, confirmDisabled, closable, handleClose, handleConfirm, handleNext]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Watermark inherit={false}>
      <RouterWrapper>
        <SimpleFormProvider
        formRef={formRef}
        name={`modal-form-${modalFormId}`}
        onFinish={() => {}}
        onFinishError={() => setLoading(false)}
        locked={loading}
        useInModal={true}
        initialValues={initialValues}
        onValuesChange={handleInternalValuesChange}
        {...formProps}
      >
        <Modal
          title={
            icon ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {icon}
                {translateX(title)}
              </span>
            ) : (
              translateX(title)
            )
          }
          open={show}
          onCancel={handleClose}
          afterClose={handleAfterClose}
          destroyOnHidden
          centered
          getContainer={document.body}
          width={width}
          mask={{ closable: maskClosable }}
          closable={closable}
          keyboard={closable}
          footer={
            <FooterButtons
              currentStep={currentStep}
              totalSteps={steps.length}
              loading={loading}
              onCancel={handleClose}
              onPrevious={handlePrevious}
              onNext={handleNext}
              onConfirm={handleConfirm}
              steps={steps}
              isConfirmStep={isConfirmStep}
              disableBack={disableBack}
              initialStep={initialStep}
              confirmDisabled={confirmDisabled}
              isSingleStep={isSingleStep}
              customLeftButtons={customLeftButtons}
            />
          }
        >
          <div ref={modalContentRef}>
            <StepForm
              ref={stepFormRef}
              steps={steps}
              currentStep={currentStep}
              visibleSteps={visibleSteps}
              visibleCurrentStep={visibleCurrentStep}
              goNext={handleNext}
              isSingleStep={isSingleStep}
            />
          </div>
        </Modal>
      </SimpleFormProvider>
    </RouterWrapper>
    </Watermark>
  );
};

// *******************************************************************************
// DYNAMIC MODAL OPENER
// *******************************************************************************
/**
 * Internal wrapper component that provides theme context to dynamically rendered modals.
 * This is necessary because modals rendered via createRoot are outside the main React tree.
 */
const DynamicModalWrapper = ({ ModalComponent, ...props }) => {
  const theme = useZustand('theme', 'currentTheme');
  const config = theme === 'dark' ? darkTheme : lightTheme;

  return (
    <ConfigProvider theme={config}>
      {/* Watermark inherit={false} prevents parent watermark from propagating to modal */}
      <Watermark inherit={false}>
        <ModalComponent {...props} />
      </Watermark>
    </ConfigProvider>
  );
};

/**
 * Creates and renders a modal dynamically without needing to add it to JSX.
 * The modal component should use GenericModal internally for consistent behavior.
 *
 * @param {React.ComponentType} ModalWrapper - The modal component to render
 * @param {Object} additionalProps - Additional props to pass to the modal (e.g., onSuccess, data)
 *
 * @example
 * // With CscTokenModal
 * openModal(CscTokenModal, { onSuccess: handleSuccess });
 *
 * @example
 * // With CertificateUploadModal
 * openModal(CertificateUploadModal, { entityId, companyCnpj, onSuccess: handleSuccess });
 */
const openModal = async (ModalWrapper, additionalProps = {}) => {
  const { createRoot } = await import('react-dom/client');

  // Create a container for the modal
  const modalContainer = document.createElement('div');

  // Try to append to #server-content first, fallback to body
  const serverContent = document.getElementById('server-content');
  const parentElement = serverContent || document.body;
  parentElement.appendChild(modalContainer);

  // Create root and render modal
  const root = createRoot(modalContainer);

  // Track modal open state
  let isModalOpen = true;

  // Render function to update modal state
  const renderModal = () => {
    root.render(
      <DynamicModalWrapper
        ModalComponent={ModalWrapper}
        show={isModalOpen}
        setShow={handleClose}
        afterClose={handleAfterClose}
        {...additionalProps}
      />
    );
  };

  // Close handler: Sets show=false to trigger closing animation
  const handleClose = () => {
    isModalOpen = false;
    renderModal();
  };

  // AfterClose handler: Called after animation completes, then cleanup DOM
  const handleAfterClose = () => {
    // Unregister from global registry when modal closes normally
    unregisterCleanup();
    setTimeout(() => {
      root.unmount();
      if (parentElement.contains(modalContainer)) {
        parentElement.removeChild(modalContainer);
      }
    }, 0);
  };

  // Register handleClose so destroyAllModals() can force-close this modal on logout
  const unregisterCleanup = appService.registerImperativeModal(handleClose);

  // Initial render
  renderModal();
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default GenericModal;
export { openModal };

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Resolve 'uniqueKey' alias to actual document field based on entityType and countryCode
 * - Brazil (BR): company → 'cnpj.value', person → 'cpf.value'
 * - Other countries: 'foreignIdentifier'
 * @param {Array} requiredFields - Array of required field names
 * @param {Object} formValues - Current form values
 * @param {Object} entityState - Fallback state from parent component (entityType, countryCode)
 */
function resolveRequiredFields(requiredFields, formValues, entityState) {
  if (!Array.isArray(requiredFields)) return [];

  // Use formValues first, fallback to entityState for entityType and countryCode
  const entityType = formValues?.entityType || entityState?.entityType;
  const countryCode = (formValues?.countryCode || entityState?.countryCode || 'BR').toUpperCase();
  const isBrazil = countryCode === 'BR';

  return requiredFields.map(field => {
    if (field === 'uniqueKey') {
      if (isBrazil) {
        return entityType === 'company' ? 'cnpj.value' : 'cpf.value';
      }
      return 'foreignIdentifier';
    }
    return field;
  });
}

/**
 * Get nested value from object using dot notation path
 * e.g., getNestedValue({ cnpj: { value: '123' } }, 'cnpj.value') => '123'
 */
function getNestedValue(obj, path) {
  if (!obj || !path) return undefined;

  const keys = path.split('.');
  let value = obj;

  for (const key of keys) {
    if (value === null || value === undefined) return undefined;
    value = value[key];
  }

  return value;
}

/**
 * Check if a value is considered empty for required field validation
 * Handles: undefined, null, empty string, empty array
 */
function isValueEmpty(value) {
  if (value === undefined || value === null) return true;
  if (value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}
